import { Button, CircularProgress } from '@material-ui/core';
import { TrashIcon, UserMinusIcon, KeyIcon } from '@phosphor-icons/react';
import { Layout } from '@orientaction/components';
import clsx from 'clsx';
import { FC } from 'react';
import ConfirmActionModal from '../shared/components/ConfirmActionModal';
import GeneratedPasswordModal from './components/GeneratedPasswordModal';
import PrestationsTab from './components/PrestationsTab';
import ProfileTab from './components/ProfileTab';
import ResourceResultsTab from './components/ResourceResultsTab';
import TestResultsTab from './components/TestResultsTab';
import {
  BENEFICIARY_PROFILE_TABS,
  buildDeletePrestationText,
  DELETE_PRESTATION_CONFIRM,
  DELETE_PRESTATION_TITLE,
  DELETE_USER_CONFIRM,
  DELETE_USER_TEXT,
  DELETE_USER_TITLE,
  DISABLE_USER_CONFIRM,
  DISABLE_USER_TEXT,
  DISABLE_USER_TITLE,
  ENABLE_USER_CONFIRM,
  ENABLE_USER_TEXT,
  ENABLE_USER_TITLE,
  STATUS_ACTIVE,
  STATUS_INACTIVE,
} from './constants';
import { useStyles } from './styles';
import { useBeneficiaryProfile } from './useBeneficiaryProfile';

const BeneficiaryProfile: FC = () => {
  const classes = useStyles();
  const page = useBeneficiaryProfile();
  const fullName =
    `${page.user?.firstname || ''} ${page.user?.lastname || ''}`.trim() || 'Bénéficiaire';
  const isActive = Boolean(page.user?.isActivated);
  const confirmCopy =
    page.confirmModal.type === 'delete'
      ? { title: DELETE_USER_TITLE, text: DELETE_USER_TEXT, confirmLabel: DELETE_USER_CONFIRM }
      : page.confirmModal.type === 'enable'
      ? { title: ENABLE_USER_TITLE, text: ENABLE_USER_TEXT, confirmLabel: ENABLE_USER_CONFIRM }
      : { title: DISABLE_USER_TITLE, text: DISABLE_USER_TEXT, confirmLabel: DISABLE_USER_CONFIRM };

  return (
    <Layout isaddBackground={true}>
      <div className={classes.page}>
        <button
          type="button"
          className={classes.backLink}
          onClick={page.editingProfile ? () => page.setEditingProfile(false) : page.goBack}>
          {page.editingProfile ? '← Retour' : '← Retour aux bénéficiaires'}
        </button>

        {page.loading ? (
          <div className={classes.loader}>
            <CircularProgress />
          </div>
        ) : (
          <>
            {!page.editingProfile && (
              <>
                <div className={classes.titleRow}>
                  <h1 className={classes.title}>{fullName}</h1>
                  <span
                    className={clsx(
                      classes.statusTag,
                      isActive ? classes.statusActive : classes.statusInactive
                    )}>
                    {isActive ? STATUS_ACTIVE : STATUS_INACTIVE}
                  </span>
                </div>

                <div className={classes.tabs}>
                  {BENEFICIARY_PROFILE_TABS.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      className={clsx(classes.tab, page.tab === item.id && classes.tabActive)}
                      onClick={() => page.setTab(item.id)}>
                      {item.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            {page.tab === 'profil' && (
              <ProfileTab
                user={page.user}
                editing={page.editingProfile}
                onStartEdit={() => page.setEditingProfile(true)}
                onUserUpdated={page.fetchUser}
                onEditingChange={page.setEditingProfile}
              />
            )}
            {page.tab === 'prestations' && (
              <PrestationsTab
                user={page.user}
                onDeletePrestation={page.askDeletePrestation}
                onAddPrestation={page.goToAddPrestation}
                onEditPrestation={page.goToEditPrestation}
              />
            )}
            {page.tab === 'resultats-tests' && (
              <TestResultsTab
                user={page.user}
                assignmentFilter={page.assignmentFilter.filter}
                assignmentFilterReady={page.assignmentFilter.ready}
              />
            )}
            {page.tab === 'resultats-ressources' && (
              <ResourceResultsTab
                user={page.user}
                assignmentFilter={page.assignmentFilter.filter}
                assignmentFilterReady={page.assignmentFilter.ready}
              />
            )}
            {page.tab !== 'profil' &&
              page.tab !== 'prestations' &&
              page.tab !== 'resultats-tests' &&
              page.tab !== 'resultats-ressources' && (
                <div className={classes.card}>
                  <p className={classes.emptyTab}>Contenu à venir</p>
                </div>
              )}

            {page.tab === 'profil' && !page.editingProfile && (
              <div className={classes.actions}>
                <Button
                  className={classes.actionBtn}
                  startIcon={<KeyIcon size={18} />}
                  disabled={page.generatingPassword}
                  onClick={page.generatePassword}>
                  {page.generatingPassword ? 'Génération...' : 'Générer un mot de passe aléatoire'}
                </Button>
                <Button
                  className={classes.actionBtn}
                  startIcon={<UserMinusIcon size={18} />}
                  onClick={page.askDisable}>
                  {isActive ? "Désactiver l'utilisateur" : "Réactiver l'utilisateur"}
                </Button>
                <Button
                  className={classes.actionBtn}
                  startIcon={<TrashIcon size={18} />}
                  onClick={page.askDelete}>
                  Supprimer définitivement
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <GeneratedPasswordModal
        open={page.passwordModal.open}
        password={page.passwordModal.password}
        onClose={page.closePasswordModal}
      />

      <ConfirmActionModal
        open={page.confirmModal.open}
        title={confirmCopy.title}
        text={confirmCopy.text}
        confirmLabel={confirmCopy.confirmLabel}
        confirming={page.confirming}
        onClose={page.closeConfirmModal}
        onConfirm={page.confirmAction}
      />

      <ConfirmActionModal
        open={page.prestationConfirm.open}
        title={DELETE_PRESTATION_TITLE}
        text={buildDeletePrestationText(
          page.prestationConfirm.service?.name || '',
          page.prestationConfirm.consultantName
        )}
        confirmLabel={DELETE_PRESTATION_CONFIRM}
        confirming={page.deletingPrestation}
        onClose={page.closePrestationConfirm}
        onConfirm={page.confirmDeletePrestation}
      />
    </Layout>
  );
};

export default BeneficiaryProfile;
